import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Form, Modal, FloatingLabel } from "react-bootstrap";
import { Formik, FormikErrors, FormikTouched } from "formik";
import { useChangePasswordMutation } from "@me-team/host/main/changePassword/graphql/changePassword.hooks";
import { useReactiveVar } from "@apollo/client";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import { changeUserPasswordValidationSchema } from "./constants";
import { SpinnerLoader } from "@services/ui-components";

interface ModalChangeUserPasswordProps {
    show: boolean;
    close: () => void;
    toggleShowToast?: (text: string) => void;
}

interface FormValues {
    currentPasswordUser: string;
    newPasswordUser: string;
    confirmPasswordUser: string;
}
const ModalChangeUserPassword: React.FC<ModalChangeUserPasswordProps> = ({ show, close, toggleShowToast }) => {
    const { t } = useTranslation();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);

    const [showCurrentPassword, setShowCurrentPassword] = useState<boolean>(false);
    const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);

    const [capsLock, setCapsLock] = useState({
        currentPasswordUser: false,
        newPasswordUser: false,
        confirmPasswordUser: false,
    });

    const [changePassword] = useChangePasswordMutation();

    const handleCapsLock = (e: React.KeyboardEvent<HTMLInputElement>) => {
        const field = e.currentTarget.name;
        const isOn = e.getModifierState("CapsLock");
        setCapsLock((prev) => ({
            ...prev,
            [field]: isOn,
        }));
    };

    const showError = (
        field: keyof FormValues,
        touched: FormikTouched<FormValues>,
        errors: FormikErrors<FormValues>,
        submitCount: number
    ) => {
        return (touched[field] || submitCount > 0) && errors[field];
    };

    return (
        <>
            <Modal centered show={show} onHide={close}>
                <Modal.Header closeButton className="border-0">
                    <Modal.Title className="fs-5">{t("Change Password")}</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <Formik
                        initialValues={{
                            currentPasswordUser: "",
                            newPasswordUser: "",
                            confirmPasswordUser: "",
                        }}
                        validationSchema={changeUserPasswordValidationSchema(t)}
                        onSubmit={async (values, { setFieldError, resetForm }) => {
                            try {
                                await changePassword({
                                    variables: {
                                        input: {
                                            currentPassword: values.currentPasswordUser,
                                            newPassword: values.newPasswordUser,
                                        },
                                        companyId: Number(currentCompanyId),
                                    },
                                    onCompleted: () => {
                                        resetForm();
                                        close();
                                        toggleShowToast?.(t("Password changed."));
                                    },
                                });
                            } catch (error: any) {
                                const code = error?.graphQLErrors?.[0]?.extensions?.category;
                                if (code === "user.current_password.invalid") {
                                    setFieldError("currentPasswordUser", t("Incorrect current password"));
                                    return;
                                }
                                setFieldError("currentPasswordUser", t("Something went wrong"));
                            }
                        }}
                    >
                        {({
                            values,
                            isSubmitting,
                            handleChange,
                            handleBlur,
                            handleSubmit,
                            errors,
                            touched,
                            submitCount,
                        }) => (
                            <Form onSubmit={handleSubmit}>
                                <Form.Group className="input-change-password mb-3">
                                    <FloatingLabel
                                        controlId="currentPasswordUser"
                                        label={t("Current password")}
                                        className="mb-1 position-relative"
                                    >
                                        <Form.Control
                                            type={showCurrentPassword ? "text" : "password"}
                                            name="currentPasswordUser"
                                            value={values.currentPasswordUser}
                                            placeholder=" "
                                            className="fs-16 pe-5"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            onKeyDown={handleCapsLock}
                                            autoComplete="new-password"
                                            isInvalid={touched.currentPasswordUser && !!errors.currentPasswordUser}
                                        />

                                        <button
                                            type="button"
                                            onClick={() => setShowCurrentPassword((prev) => !prev)}
                                            className="position-absolute top-50 translate-middle-y end-0 me-3 bg-transparent border-0 p-0 z-3"
                                        >
                                            <i
                                                className={`bi ${showCurrentPassword ? "bi-eye" : "bi-eye-slash"} fs-16`}
                                            ></i>
                                        </button>
                                    </FloatingLabel>

                                    <div className="d-flex justify-content-between align-items-start mt-2 mb-3">
                                        {capsLock.currentPasswordUser ? (
                                            <div className="text-orange fs-7 mt-1">{t("Caps Lock is on")}</div>
                                        ) : showError("currentPasswordUser", touched, errors, submitCount) ? (
                                            <div className="text-danger fs-7">{errors.currentPasswordUser}</div>
                                        ) : (
                                            <div></div>
                                        )}
                                        <a
                                            href={`${process.env.REACT_APP_POS_URL}/reset-password`}
                                            className="text-primary fs-7 text-decoration-none text-nowrap text-end"
                                        >
                                            {t("Forgot password?")}
                                        </a>
                                    </div>
                                </Form.Group>

                                <Form.Group className="input-change-password mb-3">
                                    <FloatingLabel
                                        controlId="newPasswordUser"
                                        label={t("New password")}
                                        className="mb-1"
                                    >
                                        <Form.Control
                                            type={showNewPassword ? "text" : "password"}
                                            name="newPasswordUser"
                                            value={values.newPasswordUser}
                                            placeholder=" "
                                            className="fs-16 pe-5"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            onKeyDown={handleCapsLock}
                                            autoComplete="new-password"
                                            isInvalid={touched.newPasswordUser && !!errors.newPasswordUser}
                                        />

                                        <button
                                            type="button"
                                            onClick={() => setShowNewPassword((prev) => !prev)}
                                            className="position-absolute top-50 translate-middle-y end-0 me-3 bg-transparent border-0 shadow-none p-0"
                                        >
                                            <i
                                                className={`bi ${showNewPassword ? "bi-eye" : "bi-eye-slash"} fs-16`}
                                            ></i>
                                        </button>
                                    </FloatingLabel>
                                    {capsLock.newPasswordUser ? (
                                        <div className="text-orange fs-7 mt-1">{t("Caps Lock is on")}</div>
                                    ) : (
                                        showError("newPasswordUser", touched, errors, submitCount) && (
                                            <Form.Control.Feedback type="invalid" className="d-block">
                                                {errors.newPasswordUser}
                                            </Form.Control.Feedback>
                                        )
                                    )}
                                </Form.Group>

                                <Form.Group className="input-change-password mb-2">
                                    <FloatingLabel
                                        controlId="confirmPasswordUser"
                                        label={t("Confirm new password")}
                                        className="mb-1"
                                    >
                                        <Form.Control
                                            type={showConfirmPassword ? "text" : "password"}
                                            name="confirmPasswordUser"
                                            value={values.confirmPasswordUser}
                                            placeholder=" "
                                            className="fs-16 pe-5"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            onKeyDown={handleCapsLock}
                                            isInvalid={touched.confirmPasswordUser && !!errors.confirmPasswordUser}
                                        />

                                        <button
                                            type="button"
                                            onClick={() => setShowConfirmPassword((prev) => !prev)}
                                            className="position-absolute top-50 translate-middle-y end-0 me-3 bg-transparent border-0 shadow-none p-0"
                                        >
                                            <i
                                                className={`bi ${showConfirmPassword ? "bi-eye" : "bi-eye-slash"} fs-16`}
                                            ></i>
                                        </button>
                                    </FloatingLabel>
                                    {capsLock.confirmPasswordUser ? (
                                        <div className="text-orange fs-7 mt-1">{t("Caps Lock is on")}</div>
                                    ) : (
                                        showError("confirmPasswordUser", touched, errors, submitCount) && (
                                            <Form.Control.Feedback type="invalid" className="d-block">
                                                {errors.confirmPasswordUser}
                                            </Form.Control.Feedback>
                                        )
                                    )}
                                </Form.Group>

                                <div className="pt-6 mt-3">
                                    <div className="d-flex gap-2">
                                        <Button variant="outline-primary" className="w-50" onClick={close}>
                                            {t("cancel")}
                                        </Button>

                                        <Button
                                            type="submit"
                                            variant="primary"
                                            className="fw-bold w-50"
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting ? (
                                                <SpinnerLoader variant="light" loadingText={t("Loading...")} />
                                            ) : (
                                                t("Change")
                                            )}
                                        </Button>
                                    </div>
                                </div>
                            </Form>
                        )}
                    </Formik>
                </Modal.Body>
            </Modal>
        </>
    );
};

export default ModalChangeUserPassword;
